import { InputText, TextBase, Tooltip, Typography } from '@carboncopyco/ui';
import { usePlaybookAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFormik } from 'formik';
import { useEffect, useRef } from 'react';
import { object, string } from 'yup';
import { usePlaybookModalState } from './PlaybookModalContext';
import { ErrorIndication } from './PlaybookPanel/ErrorIndication';

type PlaybookNameFormState = { name: string };

const validationSchema = object({
  name: string().required("Playbook name shouldn't be empty"),
});

export const PlaybookTitle = () => {
  const [hover, setHover] = useBoolean();
  const [isEditing, setIsEditing] = useBoolean();
  const { playbookName, setPlaybookName, setPlaybookNameError, playbookId } =
    usePlaybookModalState();
  const { trackChangeNameInput } = usePlaybookAnalytics(
    playbookId ? 'update_playbook_modal' : 'create_playbook_modal'
  );
  const playbookNameRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>();
  const [isInitialFocusComplete, setIsInitialFocusComplete] = useBoolean();

  if (inputRef.current && !isInitialFocusComplete) {
    inputRef.current.focus();
    inputRef.current.select();
    setIsEditing.setTrue();
    setIsInitialFocusComplete.setTrue();
  }

  const formik = useFormik<PlaybookNameFormState>({
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues: { name: playbookName },
    onSubmit: ({ name }, { setSubmitting }) => {
      if (playbookName !== name) {
        trackChangeNameInput({ playbookId });
        setPlaybookName(name);
      }
      setIsEditing.setFalse();
      setSubmitting(false);
    },
  });

  const { errors, handleSubmit, getFieldProps, getFieldMeta } = formik;
  const nameFieldProps = getFieldProps('name');
  const nameFieldMeta = getFieldMeta('name');

  useEffect(() => {
    setPlaybookNameError.set(!!Object.keys(errors).length);
  }, [errors, setPlaybookNameError]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-2.5 mb-4 w-[820px]">
        {!isEditing && (
          <Tooltip
            delay={[1000, 0]}
            trigger="mouseenter"
            noArrow
            placement="top-start"
            anchorRef={playbookNameRef}
          >
            <div className="p-3">
              <Typography
                fontSize="text-ds-200"
                lineHeight="leading-ds-200"
                color="text-ds-natural"
                fontWeight="font-ds-normal"
                letterSpacing="tracking-ds-tight"
                label={<div>{playbookName}</div>}
              />
            </div>
          </Tooltip>
        )}
        <div
          ref={playbookNameRef}
          onMouseEnter={setHover.setTrue}
          onMouseLeave={setHover.setFalse}
        >
          <InputText
            {...nameFieldProps}
            fullWidth
            ref={inputRef}
            rightElement={
              nameFieldMeta.error ? (
                <div className="pr-1">
                  <ErrorIndication label={<div>{nameFieldMeta.error}</div>} />
                </div>
              ) : undefined
            }
            state={
              nameFieldMeta.error
                ? 'error'
                : isEditing || hover
                  ? 'normal'
                  : 'invisible'
            }
            onBlur={() => handleSubmit()}
            onKeyPress={(keypress) => {
              if (keypress.key === 'Enter') {
                inputRef.current.blur();
              }
            }}
            onFocus={() => {
              if (!nameFieldMeta.error) {
                setIsEditing.setTrue();
              }
            }}
            placeholder="Add playbook name"
            size="xxxl"
          />
        </div>
        <TextBase
          color="text-ds-natural-300"
          label="Select the assets you would like to bundle and arrange them to control their opening sequence."
        />
      </div>
    </form>
  );
};
